import _ from 'lodash';
import { MouseEventHandler, TouchEventHandler, useCallback, useEffect, useRef, useState } from 'react';

export interface IPosition {
    x: number;
    y: number;
}

interface IUseDragSensorProps<T extends HTMLElement = HTMLElement> {
    onDrag?: (offset: IPosition, initial: IPosition, node: T) => void;
    onDragStart?: (ev: React.TouchEvent<T> | React.MouseEvent<T, MouseEvent>, initial: IPosition, node: T) => void;
    onDragEnd?: (ev: React.TouchEvent<T> | React.MouseEvent<T, MouseEvent>, offset: IPosition, initial: IPosition, node: T) => void;
    _onMouseDown?: MouseEventHandler<T>;
    _onTouchEnd?: TouchEventHandler<T>;
    _onTouchMove?: TouchEventHandler<T>;
    _onTouchStart?: TouchEventHandler<T>;
    sensorRef?: React.MutableRefObject<T>;
}

interface IMousePosition {
    onmousedown: boolean;
    initial: IPosition;
    mode: string;
}

const touchEvents = ['touchstart', 'touchmove', 'touchend', 'touchcancel'];
const isTouchEvent = <T = Element>(ev: React.TouchEvent<T> | React.MouseEvent<T>): ev is React.TouchEvent<T> => touchEvents.includes(ev.type);
const mouseEvents = ['mousedown', 'mouseup', 'mousemove', 'mouseover', 'mouseout', 'mouseenter', 'mouseleave'];
const isMouseEvent = <T = Element>(ev: React.TouchEvent<T> | React.MouseEvent<T>): ev is React.MouseEvent<T> => mouseEvents.includes(ev.type);

const getEventPosition = <T = Element>(ev: React.TouchEvent<T> | React.MouseEvent<T>): IPosition => {
    if (isTouchEvent(ev)) {
        const touch = ev.touches[0] || ev.changedTouches[0];
        return {
            x: touch.pageX,
            y: touch.pageY,
        }
    }
    if (isMouseEvent(ev)) {
        const e = ev as React.MouseEvent;
        const { clientX: x, clientY: y } = e;
        return {
            x,
            y,
        }
    }
}


const useDragSensor = <T extends HTMLElement = HTMLElement>({
    onDrag = _.noop,
    onDragStart = _.noop,
    onDragEnd = _.noop,
    _onMouseDown = _.noop,
    _onTouchEnd = _.noop,
    _onTouchMove = _.noop,
    _onTouchStart = _.noop,
    sensorRef: ref = null,
}: IUseDragSensorProps<T>) => {
    const [isDragStart, setDragStart] = useState(false);
    const modalRef = useRef(null);
    const [sensorNode, setSensorNode] = useState<T>(null);

    if (ref) {
        ref.current = modalRef.current;
    }

    const mousePositionRef = useRef<IMousePosition>({
        onmousedown: false,
        initial: undefined,
        mode: undefined,
    });

    const onTouchStart: TouchEventHandler<T> = useCallback(
        (ev) => {
            mousePositionRef.current.mode = 'touch';
            mousePositionRef.current.initial = getEventPosition<T>(ev);
            mousePositionRef.current.onmousedown = true;
            setDragStart(true);
            _onTouchStart(ev);
            onDragStart(ev, mousePositionRef.current.initial, sensorNode);
        },
        [_onTouchStart, onDragStart, sensorNode],
    );

    const onTouchMove: TouchEventHandler<T> = useCallback(
        (ev) => {
            mousePositionRef.current.mode = 'touch';
            _onTouchMove(ev);
            onDrag(getEventPosition<T>(ev), mousePositionRef.current.initial, sensorNode);
            ev.stopPropagation();
            return false;
        },
        [_onTouchMove, onDrag, sensorNode],
    );

    const onTouchEnd: TouchEventHandler<T> = useCallback(
        (ev) => {
            mousePositionRef.current.mode = 'touch';
            const current = getEventPosition<T>(ev);
            onDragEnd(ev, current, mousePositionRef.current.initial, sensorNode);
            mousePositionRef.current.onmousedown = false;
            mousePositionRef.current.initial = undefined;
            setDragStart(false);
            _onTouchEnd(ev);
        },
        [_onTouchEnd, onDragEnd, sensorNode],
    );

    const onMouseDown: MouseEventHandler<T> = useCallback(
        (ev) => {
            if (mousePositionRef.current.mode === 'touch') {
                ev.stopPropagation();
                return false;
            }
            mousePositionRef.current.mode = 'mouse';
            mousePositionRef.current.onmousedown = true;
            mousePositionRef.current.initial = getEventPosition<T>(ev);
            setDragStart(true);
            _onMouseDown(ev);
            onDragStart(ev, mousePositionRef.current.initial, sensorNode);
        },
        [_onMouseDown, onDragStart, sensorNode],
    );

    useEffect(() => {
        if (isDragStart) {
            const onmousemove = (ev) => {
                if (mousePositionRef.current.mode === 'touch') {
                    ev.stopPropagation();
                    return;
                }
                mousePositionRef.current.mode = 'mouse';
                if (mousePositionRef.current.onmousedown) {
                    onDrag(getEventPosition(ev), mousePositionRef.current.initial, sensorNode);
                    ev.stopPropagation();
                    return false;
                }
            };
            window.addEventListener('mousemove', onmousemove);
            const onmouseup = (ev) => {
                if (mousePositionRef.current.mode === 'touch') {
                    return;
                }
                mousePositionRef.current.mode = 'mouse';
                if (mousePositionRef.current.onmousedown) {
                    setDragStart(false);
                    onDragEnd(ev, getEventPosition(ev), mousePositionRef.current.initial, sensorNode);
                    mousePositionRef.current.onmousedown = false;
                    mousePositionRef.current.initial = undefined;
                }
            };
            window.addEventListener('mouseup', onmouseup);
            return () => {
                window.removeEventListener('mousemove', onmousemove);
                window.removeEventListener('mouseup', onmouseup);
            };
        }
    }, [isDragStart, onDrag, onDragEnd, sensorNode]);

    useEffect(() => {
        if (sensorNode) {
            sensorNode.addEventListener('touchstart', onTouchStart as unknown as EventListener);
            sensorNode.addEventListener('touchmove', onTouchMove as unknown as EventListener);
            sensorNode.addEventListener('touchend', onTouchEnd as unknown as EventListener);
            sensorNode.addEventListener('mousedown', onMouseDown as unknown as EventListener);
            const sensor = sensorNode;
            return () => {
                sensor.removeEventListener('touchstart', onTouchStart as unknown as EventListener);
                sensor.removeEventListener('touchmove', onTouchMove as unknown as EventListener);
                sensor.removeEventListener('touchend', onTouchEnd as unknown as EventListener);
                sensor.removeEventListener('mousedown', onMouseDown as unknown as EventListener);
            };
        }
    }, [onMouseDown, onTouchEnd, onTouchMove, onTouchStart, sensorNode]);

    const onRefChange = useCallback((node) => {
        setSensorNode(node);
    }, []);

    return {
        sensorRef: onRefChange, 
    };
};

export default useDragSensor;
